import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Divider,
  Grid,
  Link,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Theme,
  Typography,
} from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ErrorIcon from '@mui/icons-material/Error';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DateTime } from 'luxon';
import { Helmet } from 'react-helmet';
import { FileFormat, PayoutReportItem } from '../../gql-types.generated';
import { PayoutReportDisplayName } from '../../util/PayoutReportDisplayName';
import { selectSelectedTenantId } from '../app/AppSlice';
import { createPayoutReport } from '../home/HomeActions';
import {
  capturePayoutReport,
  capturePayoutReportCancelledInFlight,
  capturePayoutReportError,
  capturePayoutReportInFlight,
  selectPayoutReport,
  selectPayoutReportError,
  selectPayoutReportInFlight,
} from '../home/HomeSlice';
import LoadingMask from '../../components/LoadingMask';
import { fetchPayoutReports, resetPayoutReports } from './ReportsActions';
import {
  selectPayoutReportItems,
  selectPayoutReportsError,
  selectPayoutReportsLoading,
  selectPayoutReportYears,
  selectSelectedPayoutReportYear,
} from './ReportsSlice';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexFlow: 'column nowrap',
      flex: 1,
      height: '100%',
      minHeight: 350,
    },
    header: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      textAlign: 'start',
      padding: theme.spacing(2.5),
    },
    grid: {
      padding: theme.spacing(3),
    },
    yearFilters: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(1),
      marginBottom: theme.spacing(3),
    },
    yearChipSelected: {
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.uxBlue.activated,
      fontWeight: 600,
      '&:hover': {
        backgroundColor: theme.palette.uxBlue.activated,
      },
    },
    // The overflow control sits in a row of Chips, so it has to read as one of them
    // rather than as a form field. MUI's outlined Select is a text input by default -
    // taller, square cornered and on a larger type scale - which is why it towers over
    // the chips beside it until these are applied. 32px and a 16px radius are Chip's own
    // medium size metrics.
    yearDropdown: {
      height: 32,
      borderRadius: 16,
      fontSize: '0.8125rem',
      '& .MuiSelect-select': {
        display: 'flex',
        alignItems: 'center',
        height: '100%',
        boxSizing: 'border-box',
        padding: theme.spacing(0, 3, 0, 1.5),
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.uxGrey.border,
      },
      '& .MuiSelect-icon': {
        right: 2,
      },
    },
    // Matches yearChipSelected, but the border lives on the notched outline rather than
    // on the element itself, so it cannot simply reuse that class.
    yearDropdownSelected: {
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.uxBlue.activated,
      fontWeight: 600,
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
      },
      '& .MuiSelect-icon': {
        color: theme.palette.primary.main,
      },
    },
    // The menu is part of the same control, so its rows carry the chip type scale too.
    // MUI sizes MenuItem for a standalone menu - 1rem type on a 36px row - which lands a
    // year in the list noticeably larger than the same year on a chip behind it.
    yearDropdownItem: {
      fontSize: '0.8125rem',
      minHeight: 32,
    },
    reportList: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1.5),
    },
    monthAccordion: {
      border: `1px solid ${theme.palette.uxGrey.border}`,
      borderRadius: 10,
      backgroundColor: theme.palette.background.paper,
      // Suppress MUI's default divider pseudo-element so the bordered cards do
      // not gain a doubled line where they meet.
      '&:before': {
        display: 'none',
      },
      '&.Mui-expanded': {
        margin: 0,
      },
    },
    monthSummary: {
      '& .MuiAccordionSummary-content': {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
      },
    },
    monthCount: {
      color: theme.palette.text.secondary,
    },
    monthDetails: {
      padding: theme.spacing(0, 1, 1),
    },
    downloadCell: {
      width: 160,
    },
    downloadLinks: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(2),
    },
    downloadLink: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      cursor: 'pointer',
      fontWeight: 500,
    },
    downloadIcon: {
      fontSize: '1.1rem',
    },
    emptyState: {
      padding: theme.spacing(4),
      textAlign: 'center',
      color: theme.palette.text.secondary,
    },
    errorBanner: {
      backgroundColor: theme.palette.error.light,
      color: theme.palette.error.main,
      fontWeight: 500,
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(1, 3),
    },
    errorIcon: {
      marginRight: 4,
    },
  }),
);

/**
 * How many year chips are rendered before the rest fold into a dropdown.
 *
 * A cap on the CHIPS, not on the history. The page previously capped the history itself
 * because the whole of it came down in one response; now that a year is fetched per click
 * the list can be as long as the merchant's history, and only the row of chips needs to
 * stay a fixed width. Three, because the row must not wrap or scroll horizontally.
 *
 * The three are the current year and the two before it, per the work item - a fixed
 * window, not simply the three newest years a merchant happens to have. The difference
 * shows on a merchant who has stopped taking payments: their newest year is not the
 * current one, so it belongs in the dropdown with the rest of their history.
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
const YEAR_CHIP_LIMIT = 3;

const Reports: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const payoutReport = useSelector(selectPayoutReport);
  const payoutReportError = useSelector(selectPayoutReportError);
  const isFetchingPayoutUrl = useSelector(selectPayoutReportInFlight);

  // Reports for the selected year ONLY. The server windows to a single calendar year, so
  // this is never the full history and nothing here may assume otherwise.
  const payoutReports = useSelector(selectPayoutReportItems);
  const years = useSelector(selectPayoutReportYears);
  const selectedYear = useSelector(selectSelectedPayoutReportYear);
  const isLoadingReports = useSelector(selectPayoutReportsLoading);
  const payoutReportsError = useSelector(selectPayoutReportsError);
  const selectedTenantId = useSelector(selectSelectedTenantId);

  const downloadRef = useRef<HTMLAnchorElement>(null);
  // The payout report id currently being downloaded, so we know which resolved URI to act on.
  const [activeReportId, setActiveReportId] = useState<string | null>(null);

  // Reset the shared payout report state. createPayoutReport leaves the in-flight
  // flag set on success, so it must be cleared here once the download is handled.
  const clearPayoutReportState = () => {
    dispatch(capturePayoutReport(undefined));
    dispatch(capturePayoutReportError(undefined));
    dispatch(capturePayoutReportInFlight(false));
  };

  // First load, and again whenever the merchant switches tenant.
  //
  // Keyed on the tenant, NOT just on mount. Switching tenant does not unmount this page,
  // so a mount-only fetch left the previous tenant's reports on screen until a reload -
  // one merchant's payout history shown under another's name. This came for free while
  // the reports rode on the account query, which the tenant switch already refetches;
  // owning the request means owning that too.
  //
  // The reset first so the outgoing tenant's reports are gone before the new ones land,
  // rather than sitting there looking current while the request is open.
  //
  // The year is deliberately omitted: the server answers with its most recent year that
  // has data, so the page opens on the right year in one round trip rather than fetching
  // the year list and then fetching the year it names. A year selected for the previous
  // tenant would be meaningless here anyway.
  useEffect(() => {
    dispatch(resetPayoutReports());
    dispatch(fetchPayoutReports());
  }, [dispatch, selectedTenantId]);

  // Each chip is a request now. Clicks used to filter an array already in the browser,
  // which is exactly the design that forced the history cap.
  const handleYearSelect = (year: number) => {
    if (year === selectedYear) return;
    dispatch(fetchPayoutReports(year));
  };

  const handleOverflowYearSelect = (event: SelectChangeEvent<string>) => {
    const year = Number(event.target.value);
    if (Number.isInteger(year)) handleYearSelect(year);
  };

  // Only three chips fit without the row wrapping or scrolling, so the rest fold into a
  // dropdown. Split on the SERVER's year list rather than on what was fetched - that is
  // what lets a merchant reach year four, the thing this whole change exists to fix.
  //
  // A year is only given a chip if the merchant actually has reports in it, so an inactive
  // merchant is not offered three chips that lead nowhere. The slice is belt and braces:
  // the queryable range runs one year past the server's current year, so a merchant in
  // UTC+13 can legitimately hold a year the window would otherwise let through as a fourth.
  const { visibleYears, overflowYears } = useMemo(() => {
    const earliestChipYear = DateTime.utc().year - (YEAR_CHIP_LIMIT - 1);
    const chips = years.filter(year => year >= earliestChipYear).slice(0, YEAR_CHIP_LIMIT);
    return { visibleYears: chips, overflowYears: years.filter(year => !chips.includes(year)) };
  }, [years]);

  // The dropdown shows the selected year as its value when the selection is one of the
  // folded years, so a merchant on 2022 is not left with every chip looking unselected.
  const overflowSelection = selectedYear !== undefined && overflowYears.includes(selectedYear) ? String(selectedYear) : '';

  // The reports that arrived, grouped by month, most recent month first and most recent
  // report first within each month.
  //
  // Grouped on payoutCompleteTime, NOT endTime, because that is the field the server
  // windows the year on. The two disagree for a report whose period ends in one year and
  // whose payout completes in the next: it arrives in the LATER year's response, and
  // bucketing it by endTime would place it in a year that response does not cover. It
  // would then be dropped and appear under no year at all - unreachable, which is the
  // defect this whole change set exists to remove.
  //
  // There is no year filter here any more either. The server returns one calendar year,
  // so filtering again client side could only ever delete rows it deliberately sent.
  //
  // Reports are not all daily - the feed mixes daily, weekly and monthly intervals - so a
  // month can hold a mix. PayoutReportDisplayName already prefixes W/E/M/E to disambiguate.
  const monthGroups = useMemo(() => {
    // Keyed by year AND month, and labelled from the rows rather than from selectedYear.
    // selectedYear is separate state that can lag the rows it describes, and a label built
    // from it would name a year none of the reports under it belong to.
    const byMonth = new Map<string, { year: number; month: number; reports: PayoutReportItem[] }>();
    (payoutReports || []).forEach(report => {
      const completed = DateTime.fromISO(report.payoutCompleteTime).toUTC();
      if (!completed.isValid) return;
      const key = `${completed.year}-${completed.month}`;
      const bucket = byMonth.get(key);
      if (bucket) bucket.reports.push(report);
      else byMonth.set(key, { year: completed.year, month: completed.month, reports: [report] });
    });
    return Array.from(byMonth.values())
      .sort((a, b) => b.year - a.year || b.month - a.month)
      .map(group => ({
        month: group.month,
        // Luxon months are 1-based, so this needs no offset.
        label: DateTime.utc(group.year, group.month, 1).toFormat('MMMM yyyy'),
        reports: group.reports
          .slice()
          .sort((a, b) => (DateTime.fromISO(a.payoutCompleteTime) > DateTime.fromISO(b.payoutCompleteTime) ? -1 : 1)),
      }));
  }, [payoutReports]);

  // Which month accordions are open. The most recent month is expanded by default
  // so the common case - checking the latest payout - costs no clicks.
  const [expandedMonths, setExpandedMonths] = useState<number[]>([]);

  // Extracted rather than inlined into the dependency array below: a complex
  // expression there trips react-hooks/exhaustive-deps, and CRA's build promotes
  // that warning to an error under CI=true.
  const monthKeys = monthGroups.map(group => group.month).join(',');

  useEffect(() => {
    // Keyed on the year and the month set so switching years re-opens the newest
    // month rather than leaving every accordion collapsed.
    setExpandedMonths(monthGroups.length > 0 ? [monthGroups[0].month] : []);
  }, [selectedYear, monthKeys]);

  const handleMonthToggle = (month: number) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedMonths(current => (isExpanded ? [...current, month] : current.filter(open => open !== month)));
  };

  const handleDownload = (payoutReportId?: string | null, format?: FileFormat) => {
    if (payoutReportId && format) {
      setActiveReportId(payoutReportId);
      dispatch(createPayoutReport(payoutReportId, format));
    }
  };

  // Once the access URI for the requested report resolves, trigger the browser download.
  useEffect(() => {
    if (payoutReport?.uri && activeReportId && downloadRef.current) {
      downloadRef.current.href = payoutReport.uri;
      downloadRef.current.click();
      setActiveReportId(null);
      clearPayoutReportState();
    }
  }, [payoutReport, activeReportId, dispatch]);

  // On unmount, cancel any in-flight generation and reset the shared state.
  useEffect(
    () => () => {
      dispatch(capturePayoutReportCancelledInFlight(true));
      clearPayoutReportState();
      // Drop the loaded year too. Leaving it would show the previous visit's reports on
      // the next mount while the first request is still open.
      dispatch(resetPayoutReports());
    },
    [dispatch],
  );

  return (
    <Paper className={classes.root} role="region" aria-label="Reports">
      <Helmet>
        <title>Aptean Pay Merchant Portal - Reports</title>
      </Helmet>
      <LoadingMask loading={isFetchingPayoutUrl} />
      <a ref={downloadRef} style={{ display: 'none' }} />
      <Box className={classes.header}>
        <Typography variant="title">Reports</Typography>
      </Box>
      <Divider />
      {payoutReportError && (
        <Box className={classes.errorBanner}>
          <ErrorIcon className={classes.errorIcon} />
          {`${payoutReportError?.message || 'Unable to generate payout report'}. Please try again later!`}
        </Box>
      )}
      {payoutReportsError && (
        <Box className={classes.errorBanner} data-cy="report-fetch-error">
          <ErrorIcon className={classes.errorIcon} />
          {`${payoutReportsError?.message || 'Unable to load payout reports'}. Please try again later!`}
        </Box>
      )}
      <Grid className={classes.grid} container direction="column">
        {years.length > 0 && (
          <Box className={classes.yearFilters} data-cy="report-year-filters">
            {visibleYears.map(year => (
              <Chip
                key={year}
                label={year}
                variant="outlined"
                clickable
                // Chips stay enabled during a fetch. Disabling them would strand a
                // merchant who clicked the wrong year until the response lands, and the
                // request guard in ReportsActions already makes a changed mind safe.
                onClick={() => handleYearSelect(year)}
                className={selectedYear === year ? classes.yearChipSelected : undefined}
                data-cy={`report-year-${year}`}
              />
            ))}
            {overflowYears.length > 0 && (
              <Select
                value={overflowSelection}
                onChange={handleOverflowYearSelect}
                displayEmpty
                variant="outlined"
                className={[classes.yearDropdown, overflowSelection ? classes.yearDropdownSelected : ''].join(' ').trim()}
                renderValue={value => value || 'Earlier years'}
                inputProps={{ 'aria-label': 'Earlier years' }}
                data-cy="report-year-overflow"
              >
                {overflowYears.map(year => (
                  <MenuItem key={year} value={String(year)} className={classes.yearDropdownItem} data-cy={`report-year-${year}`}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            )}
          </Box>
        )}
        {monthGroups.length > 0 ? (
          <Box className={classes.reportList} data-cy="report-list">
            {monthGroups.map(group => (
              <Accordion
                key={group.month}
                className={classes.monthAccordion}
                expanded={expandedMonths.includes(group.month)}
                onChange={handleMonthToggle(group.month)}
                disableGutters
                elevation={0}
                data-cy={`report-month-${selectedYear}-${String(group.month).padStart(2, '0')}`}
              >
                <AccordionSummary
                  className={classes.monthSummary}
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`report-month-panel-${group.month}`}
                  id={`report-month-header-${group.month}`}
                >
                  <Typography variant="subtitle1" fontWeight={600}>
                    {group.label}
                  </Typography>
                  <Typography variant="body2" className={classes.monthCount} data-cy="report-month-count">
                    {group.reports.length === 1 ? '1 report' : `${group.reports.length} reports`}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails className={classes.monthDetails} id={`report-month-panel-${group.month}`}>
                  {/* Two columns today. Kept as a table rather than a list because the
                      payout amount is the likely next column, and the schema does not
                      expose it yet (it exists on the payout report document but not on
                      PayoutReportItem). A "Period" column was removed deliberately:
                      startTime/endTime, payoutCreateTime and payoutCompleteTime all
                      collapse to the same calendar date for ~98% of production reports,
                      so any of them merely repeats the Report column. */}
                  <Table size="small" aria-label={`Payout reports for ${group.label}`}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Report</TableCell>
                        <TableCell className={classes.downloadCell}>Download</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {group.reports.map(report => (
                        <TableRow key={report.payoutReportId ?? report.endTime} data-cy="report-item" hover>
                          <TableCell>{PayoutReportDisplayName(report.startTime, report.endTime)}</TableCell>
                          <TableCell className={classes.downloadCell}>
                            {report.payoutReportId ? (
                              <Box className={classes.downloadLinks}>
                                {[FileFormat.Csv, FileFormat.Pdf].map(format => (
                                  <Link
                                    key={format}
                                    component="button"
                                    type="button"
                                    underline="hover"
                                    className={classes.downloadLink}
                                    disabled={isFetchingPayoutUrl}
                                    onClick={() => handleDownload(report.payoutReportId, format)}
                                    data-cy={`report-download-${format}`}
                                  >
                                    <FileDownloadOutlinedIcon className={classes.downloadIcon} />
                                    {format === FileFormat.Pdf ? 'PDF' : 'CSV'}
                                  </Link>
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="textSecondary">
                                Unavailable
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        ) : null}
        {/* Suppressed when the request failed. An empty list and a failed request look
            identical from here, and "No reports available" is a claim we cannot make about
            data we never received - the error banner above is the honest answer. */}
        {!isLoadingReports && !payoutReportsError && monthGroups.length === 0 && (
          <Box className={classes.emptyState} data-cy="report-empty">
            <Typography variant="body1">
              {selectedYear === undefined ? 'No reports available.' : `No reports available for ${selectedYear}.`}
            </Typography>
          </Box>
        )}
      </Grid>
    </Paper>
  );
};

export default Reports;
